import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../ui/Button.jsx';
import { NAV, PRIMARY_CTA, SITE } from '../../content/site.js';
import logo from '../../assets/logo-lockup.webp';

/*
 * With no nav items there is nothing for a drawer to contain, so the hamburger
 * is dropped and the one action moves into the bar itself at every width —
 * putting a single button behind a menu toggle costs a tap for nothing. Add an
 * entry to NAV and the desktop nav and the mobile drawer both come back.
 */
const HAS_NAV = NAV.length > 0;

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  // Close the drawer on navigation, otherwise it stays open over the new page.
  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  // Escape to close, and lock body scroll while the drawer covers the page.
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the drawer so keyboard and screen-reader users land there.
    panelRef.current?.querySelector('a, button')?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-deep">
      <div className="wrap flex items-center justify-between py-4">
        {/* The lockup already contains the wordmark, so the text alongside it
            was removed — printing "Chickrah" twice. alt stays empty because the
            link itself is labelled; giving both would announce the name twice
            to a screen reader. Height is fixed and width auto so the aspect
            ratio can never be squeezed by a flex parent. */}
        <Link to="/" className="flex items-center" aria-label={`${SITE.name} home`}>
          <img
            src={logo}
            alt=""
            width="555"
            height="160"
            className="h-8 w-auto sm:h-9"
          />
        </Link>

        {HAS_NAV && (
          <nav aria-label="Main" className="hidden items-center gap-8 lg:flex">
            {NAV.map((item) => (
              <NavItem key={item.label} item={item} />
            ))}
          </nav>
        )}

        <div className={HAS_NAV ? 'hidden items-center gap-3 lg:flex' : 'flex items-center gap-3'}>
          <Button to={PRIMARY_CTA.to} variant="primary" size="sm">
            {PRIMARY_CTA.label}
          </Button>
        </div>

        {HAS_NAV && (
          <button
            ref={toggleRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Close menu' : 'Open menu'}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-white transition hover:bg-white/10 lg:hidden"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              {open ? (
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              ) : (
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              )}
            </svg>
          </button>
        )}
      </div>

      {HAS_NAV && (
        <div
          id="mobile-nav"
          ref={panelRef}
          hidden={!open}
          className="border-t border-white/10 bg-deep lg:hidden"
        >
          <nav aria-label="Mobile" className="wrap flex flex-col py-4">
            {NAV.map((item) => (
              <NavItem key={item.label} item={item} mobile />
            ))}
            <div className="mt-4 flex flex-col gap-3">
              <Button to={PRIMARY_CTA.to} variant="primary" className="w-full">
                {PRIMARY_CTA.label}
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function NavItem({ item, mobile = false }) {
  const cls = mobile
    ? 'py-3 text-[15px] font-medium text-mist hover:text-white'
    : 'text-sm font-medium text-mist transition hover:text-white';

  return (
    <Link to={item.to} className={cls}>
      {item.label}
    </Link>
  );
}
